import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChecklistCategory, useChecklistStore } from '../../../../stores/checklistStore';

import { Button, TextInput } from 'flowbite-react';
import { TrashIcon, PencilIcon, CheckIcon, PlusIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline'; // Added Icons
import QuestionList from './QuestionList';
import DeleteConfirmationModal from './DeleteConfirmationModal'; // Import Modal




import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

interface Props {
    category: ChecklistCategory;
    centerId: string;
    variant?: 'default' | 'onboarding';
    index?: number;
}

const ONBOARDING_CATEGORY_COLORS: Record<string, string> = {
    hook: '#16a34a',
    'key questions': '#3b82f6',
    qualification: '#ef4444',
};

const CategoryItem: React.FC<Props> = ({ category, centerId, variant = 'default', index }) => {
    const {
        updateCategory,
        deleteCategory,
        questions: allQuestions,
        createQuestion,
        categories, // Added
        reorderCategories, // Added
        reorderQuestions // Added
    } = useChecklistStore();
    const { t } = useTranslation();

    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(category.name);
    const [newQuestionText, setNewQuestionText] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // Modal state

    const normalizedName = category.name.trim().toLowerCase();
    const visualColor = variant === 'onboarding'
        ? (ONBOARDING_CATEGORY_COLORS[normalizedName] ?? category.color ?? '#ad330a')
        : (category.color ?? '#3B82F6');

    const categoryQuestions = allQuestions
        .filter(q => q.category_id === category.id)
        .sort((a, b) => {
            if (a.order_index !== b.order_index) return a.order_index - b.order_index;
            return (a.id || '').localeCompare(b.id || '');
        });

    // --- Dnd Sensors ---
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over) return;

        if (active.id !== over.id) {
            // Reorder within this category
            const oldIndex = categoryQuestions.findIndex(q => q.id === active.id);
            const overIndex = categoryQuestions.findIndex(q => q.id === over.id);

            if (oldIndex !== -1 && overIndex !== -1) {
                const newOrder = arrayMove(categoryQuestions, oldIndex, overIndex);
                reorderQuestions(newOrder);
            }
        }
    };

    const handleMoveUp = (e: React.MouseEvent) => {
        e.stopPropagation();
        const currentIndex = categories.findIndex(c => c.id === category.id);
        if (currentIndex > 0) {
            const newCategories = [...categories];
            [newCategories[currentIndex], newCategories[currentIndex - 1]] = [newCategories[currentIndex - 1], newCategories[currentIndex]];
            reorderCategories(newCategories);
        }
    };

    const handleMoveDown = (e: React.MouseEvent) => {
        e.stopPropagation();
        const currentIndex = categories.findIndex(c => c.id === category.id);
        if (currentIndex < categories.length - 1) {
            const newCategories = [...categories];
            [newCategories[currentIndex], newCategories[currentIndex + 1]] = [newCategories[currentIndex + 1], newCategories[currentIndex]];
            reorderCategories(newCategories);
        }
    };

    const handleSaveName = () => {
        if (editName.trim() !== category.name) {
            updateCategory(category.id, { name: editName });
        }
        setIsEditing(false);
    };

    const handleColorChange = (newColor: string) => {
        updateCategory(category.id, { color: newColor });
    };

    const confirmDeleteCategory = () => {
        const confirmed = window.confirm(
            t('checklistCustomization.modals.deleteCategoryMessage', {
                defaultValue: 'Are you sure you want to delete this category? The questions will be unassociated.',
            })
        );

        if (confirmed) {
            void deleteCategory(category.id, centerId);
        }
    };

    return (
        <div
            style={variant === 'onboarding' ? undefined : {
                borderColor: category.color ? `${category.color}40` : undefined,
                backgroundColor: category.color ? `${category.color}05` : 'rgba(255,255,255,0.5)'
            }}
            className={`group relative mb-4 transition-all duration-300 ${
                variant === 'onboarding'
                    ? 'overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] dark:border-neutral-700 dark:bg-neutral-800'
                    : 'rounded-xl border border-x-neutral-200 border-y-neutral-200 hover:shadow-lg dark:border-neutral-750'
            }`}
        >
            <div
                dir={variant === 'onboarding' ? 'ltr' : undefined}
                className={`flex items-center justify-between ${variant === 'onboarding'
                    ? 'rounded-t-xl border-b border-neutral-200 bg-white px-6 py-4 dark:border-neutral-700 dark:bg-neutral-800'
                    : 'rounded-t-xl border-b border-neutral-100 bg-white/80 p-4 backdrop-blur-sm dark:border-neutral-700/50 dark:bg-neutral-800/90'
                }`}
            >
                <div className="flex flex-1 items-center gap-3">
                    {/* Reorder Buttons */}
                    <div className="flex flex-col -space-y-1">
                        <button
                            onClick={handleMoveUp}
                            className={`rounded p-0.5 transition-colors ${
                                variant === 'onboarding'
                                    ? 'text-neutral-400 hover:bg-neutral-100 hover:text-brand-600 dark:text-neutral-500 dark:hover:bg-neutral-700 dark:hover:text-brand-300'
                                    : 'text-neutral-400 hover:bg-brand-50 hover:text-brand-500'
                            }`}
                            disabled={categories.findIndex(c => c.id === category.id) === 0}
                        >
                            <ChevronUpIcon className="h-3 w-3" />
                        </button>
                        <button
                            onClick={handleMoveDown}
                            className={`rounded p-0.5 transition-colors ${
                                variant === 'onboarding'
                                    ? 'text-neutral-400 hover:bg-neutral-100 hover:text-brand-600 dark:text-neutral-500 dark:hover:bg-neutral-700 dark:hover:text-brand-300'
                                    : 'text-neutral-400 hover:bg-brand-50 hover:text-brand-500'
                            }`}
                            disabled={categories.findIndex(c => c.id === category.id) === categories.length - 1}
                        >
                            <ChevronDownIcon className="h-3 w-3" />
                        </button>
                    </div>

                    {isEditing ? (
                        <div className="flex flex-1 items-center gap-2">
                            <input
                                type="color"
                                value={visualColor}
                                onChange={(e) => handleColorChange(e.target.value)}
                                className="h-9 w-9 cursor-pointer rounded-lg border-none bg-transparent p-0 shadow-sm transition-transform hover:scale-105"
                                title="Change Category Color"
                            />
                            <TextInput
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="flex-1"
                                sizing="sm"
                                color="gray"
                                dir="rtl"
                                theme={{ field: { input: { base: 'w-full rounded-lg border-neutral-300 bg-neutral-50 p-2 text-right text-sm focus:border-brand-500 focus:ring-brand-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white dark:placeholder-neutral-400 dark:focus:border-brand-500 dark:focus:ring-brand-500' } } }}
                            />
                            <Button size="xs" color="success" onClick={handleSaveName} className="rounded-lg">
                                <CheckIcon className="h-4 w-4" />
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <div
                                className="flex h-8 w-8 items-center justify-center rounded-lg shadow-sm"
                                style={{ backgroundColor: visualColor }}
                            >
                                {variant === 'onboarding' && index !== undefined ? (
                                    <span className="text-xs font-bold text-white">{index + 1}</span>
                                ) : (
                                    <span className="font-mono text-xs font-bold text-white">#</span>
                                )}
                            </div>
                            <div className="flex flex-col">
                                {variant === 'onboarding' && index !== undefined && (
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
                                        שלב {index + 1}
                                    </span>
                                )}
                                <h4 className={`font-bold tracking-tight ${variant === 'onboarding' ? 'text-right text-[18px] text-neutral-900 dark:text-neutral-100' : 'text-lg text-gray-800 dark:text-white'}`}>
                                    {category.name}
                                </h4>
                            </div>
                        </div>
                    )}
                </div>

                <div className={`flex items-center gap-2 ${variant === 'onboarding' ? 'ms-4 opacity-100 sm:opacity-0 sm:group-hover:opacity-100' : 'ml-4 opacity-0 group-hover:opacity-100'} transition-opacity duration-200`}>
                    <Button size="xs" color="light" pill onClick={() => setIsEditing(!isEditing)} className="border-none hover:bg-neutral-100 dark:bg-neutral-700 dark:hover:bg-neutral-600">
                        <PencilIcon className="h-4 w-4 text-neutral-500" />
                    </Button>
                    <Button
                        size="xs"
                        color="light"
                        pill
                        onClick={(event: MouseEvent<HTMLButtonElement>) => {
                            if (variant === 'onboarding') {
                                (event.currentTarget as HTMLButtonElement).blur();
                                confirmDeleteCategory();
                                return;
                            }

                            setIsDeleteModalOpen(true);
                        }}
                        className="border-none hover:bg-red-50 dark:bg-neutral-700 dark:hover:bg-red-900/20"
                    >
                        <TrashIcon className="h-4 w-4 text-red-500" />
                    </Button>
                </div>
            </div>

            <div
                dir={variant === 'onboarding' ? 'rtl' : undefined}
                className={variant === 'onboarding' ? 'rounded-b-xl bg-white px-4 py-4 dark:bg-neutral-800' : 'rounded-b-xl p-4'}
                style={variant === 'onboarding' ? undefined : { backgroundColor: category.color ? `${category.color}08` : 'rgba(0,0,0,0.02)' }}
            >
                {/* Internal Question List */}
                <div className="space-y-2 mb-4 min-h-[50px]">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <QuestionList questions={categoryQuestions} categoryColor={visualColor} variant={variant} />
                    </DndContext>
                    {categoryQuestions.length === 0 && (
                        <div className={`rounded-lg py-6 text-center ${variant === 'onboarding' ? 'border border-dashed border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900/40' : 'border-2 border-dashed border-neutral-200 dark:border-neutral-700'}`}>
                            <p className="text-sm italic text-neutral-500 dark:text-neutral-400">{t('checklistCustomization.placeholders.noQuestionsInCategory') || "No questions yet"}</p>
                        </div>
                    )}
                </div>

                {/* Add Question Input inside Category - styled better */}
                <div className={`mt-2 flex items-center gap-2 ${variant === 'onboarding'
                    ? 'rounded-lg bg-neutral-100 px-2 py-2 dark:bg-neutral-700/70'
                    : 'rounded-lg border border-neutral-200 bg-white p-1.5 shadow-sm transition-all focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-100 dark:border-neutral-700 dark:bg-neutral-800'
                }`}>
                    {variant === 'onboarding' ? null : <PlusIcon className="ml-2 h-4 w-4 text-neutral-400" />}
                    <TextInput
                        placeholder={
                            variant === 'onboarding'
                                ? `${t('checklistCustomization.actions.addCategory', { defaultValue: 'הוסף' }).replace('קטגוריה', '').trim()} ${category.name}...`
                                : t('checklistCustomization.placeholders.addQuestionToCategory')
                        }
                        className="flex-1 !border-none !ring-0 !shadow-none bg-transparent"
                        sizing="sm"
                        value={newQuestionText}
                        onChange={(e) => setNewQuestionText(e.target.value)}
                        theme={{ field: { input: { base: `w-full !border-none !ring-0 !shadow-none bg-transparent text-right text-sm text-neutral-900 focus:ring-0 dark:text-neutral-100 ${variant === 'onboarding' ? 'placeholder:text-neutral-500 dark:placeholder:text-neutral-400 !bg-transparent' : ''}` } } }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && newQuestionText.trim()) {
                                createQuestion(centerId, newQuestionText, category.id);
                                setNewQuestionText('');
                            }
                        }}
                    />
                    <Button
                        size="xs"
                        className={variant === 'onboarding'
                            ? 'rounded-lg border-none bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-950/30 dark:text-brand-300 dark:hover:bg-brand-950/50'
                            : 'rounded-lg border-none bg-gradient-to-r from-brand-500 to-brand-600 text-white hover:opacity-90'
                        }
                        disabled={!newQuestionText.trim()}
                        onClick={() => {
                            if (newQuestionText.trim()) {
                                createQuestion(centerId, newQuestionText, category.id);
                                setNewQuestionText('');
                            }
                        }}>
                        <span className="px-2 text-xs font-semibold">{variant === 'onboarding' ? 'הוסף' : (t('common.add') || 'Add')}</span>
                    </Button>
                </div>
            </div>

            {variant === 'onboarding' ? null : (
                <DeleteConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={() => {
                        void deleteCategory(category.id, centerId);
                        setIsDeleteModalOpen(false);
                    }}
                    title={t('checklistCustomization.modals.deleteCategoryTitle', { defaultValue: 'Delete Category' })}
                    message={t('checklistCustomization.modals.deleteCategoryMessage', { defaultValue: 'Are you sure you want to delete this category? The questions will be unassociated.' })}
                />
            )}
        </div>
    );
};

export default CategoryItem;
